import React, { useCallback, useEffect, useRef } from 'react';
import CloseIcon from '@material-ui/icons/Close';
import { Button, DialogContentText } from '@material-ui/core';
import { useIntl } from 'react-intl';
import {
  StyledDialogTitle,
  StyledIconButton,
  StyledDialogActions,
  StyledDialogContent,
  CustomeTextField,
  CustomDialog,
  EyeIcon,
} from './styles';
import { UpdatePassword } from '../../../services/auth';
import { GetPasswordConfigurations } from '../../../services/staff';
import { useAuth } from '../../../hooks/auth';
import { useHistory } from 'react-router-dom';
import { useToast } from '../../../hooks/toast';
import { InputAdornment } from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';

const ChangePasswordDialog = ({ open, handleCancel }) => {
  const intl = useIntl();
  const { user, portal, setPortal } = useAuth();
  const history = useHistory();
  const { addToast } = useToast(false);

  const [oldPassword, setOldPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [oldPasswordError, setOldPasswordError] = React.useState('');
  const [newPasswordError, setNewPasswordError] = React.useState('');
  const [confirmPasswordError, setConfirmPasswordError] = React.useState('');
  const [passwordRules, setPasswordRules] = React.useState({}); //intl.formatMessage({ id: 'passwordRules' })
  const [passwordRulesString, setPasswordRulesString] = React.useState('');

  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showOldPassword, setShowOldPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  const [newPasswordType, setNewPasswordType] = React.useState('password');

  const dialogRef = useRef(null);
  useEffect(() => {
    if (open && dialogRef.current) {
      const firstFocusableElement = dialogRef.current.querySelector(
        'input, button, a, [tabindex]:not([tabindex="-1"])'
      );
      if (firstFocusableElement) {
        firstFocusableElement.focus();
      }
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      handlePasswordRules();
    }
  }, [open]);

  useEffect(() => {
    if (Object.keys(passwordRules).length > 0) {
      generatePasswordRuleString();
    }
  }, [passwordRules]);

  const handlePasswordRules = async () => {
    const result = await GetPasswordConfigurations({});
    if (
      result.GetPasswordConfigurationsResult &&
      result.passwordConfigurations
    ) {
      const passwordConfigurations = result.passwordConfigurations.reduce(
        (acc, rule) => {
          acc[rule.Key] = rule.Value;
          return acc;
        },
        {}
      );

      if (passwordConfigurations) {
        setPasswordRules(passwordConfigurations);
      }
    }
  };

  const generatePasswordRuleString = () => {
    const minPasswordChars = passwordRules['Minimum Password Characters'];
    const minUpperChars = passwordRules['Minimum Uppercase Characters'];
    const minLowerChars = passwordRules['Minimum Lowercase Characters'];
    const minSpecialChars = passwordRules['Minimum Special Characters'];
    const minNumbers = passwordRules['Minimum Numbers'];
    const specialChars = passwordRules['Special Characters'];

    const formattedPasswordRulesString = intl.formatMessage(
      {
        id: 'newPasswordRules',
      },
      {
        minPasswordChars,
        minUpperChars,
        minLowerChars,
        minSpecialChars,
        specialChars,
        minNumbers,
      }
    );
    setPasswordRulesString(formattedPasswordRulesString);
  };

  const logout = useCallback(() => {
    localStorage.clear();
    sessionStorage.clear();
    setPortal(null);
    history.push('/');
  }, [history, setPortal]);

  const validatePassword = (password) => {
    const minUpperChars = passwordRules['Minimum Uppercase Characters'];
    const minLowerChars = passwordRules['Minimum Lowercase Characters'];
    const minSpecialChars = passwordRules['Minimum Special Characters'];
    const minNumbers = passwordRules['Minimum Numbers'];

    const minLength = passwordRules['Minimum Password Characters']; //12
    const uppercase = /[A-Z]/;
    const lowercase = /[a-z]/;
    const number = /[0-9]/;
    const specialChar = /[^A-Za-z0-9\s]/;
    const invalidChar = /[\\/]/;

    if (password) {
      if (password.length < minLength) {
        return intl.formatMessage({ id: 'PasswordTooShort' });
      }
      if (minUpperChars > 0 && !uppercase.test(password)) {
        return intl.formatMessage({ id: 'PasswordUppercaseRequired' });
      }
      if (minLowerChars > 0 && !lowercase.test(password)) {
        return intl.formatMessage({ id: 'PasswordLowercaseRequired' });
      }
      if (minNumbers > 0 && !number.test(password)) {
        return intl.formatMessage({ id: 'PasswordNumberRequired' });
      }
      if (minSpecialChars > 0 && !specialChar.test(password)) {
        return intl.formatMessage({ id: 'PasswordSpecialCharRequired' });
      }
      if (invalidChar.test(password)) {
        return intl.formatMessage({ id: 'PasswordInvalidChar' });
      }

      const uppercaseletters = /[A-Z]/g;
      const upperCaseCount = (password.match(uppercaseletters) || []).length;
      if (upperCaseCount < minUpperChars) {
        return intl.formatMessage({ id: 'PasswordUppercaseRequired' });
      }

      const lowercaseletters = /[a-z]/g;
      const lowerCaseCount = (password.match(lowercaseletters) || []).length;
      if (lowerCaseCount < minLowerChars) {
        return intl.formatMessage({ id: 'PasswordLowercaseRequired' });
      }

      const numberletters = /[0-9]/g;
      const minNumbersCount = (password.match(numberletters) || []).length;
      if (minNumbersCount < minNumbers) {
        return intl.formatMessage({ id: 'PasswordNumberRequired' });
      }

      const specialCharletters = /[^A-Za-z0-9\s]/g;
      const minSpecialCharsCount = (password.match(specialCharletters) || [])
        .length;
      if (minSpecialCharsCount < minSpecialChars) {
        return intl.formatMessage({ id: 'PasswordSpecialCharRequired' });
      }

      return '';
    } else {
      return intl.formatMessage({ id: 'NewPasswordRequired' });
    }
  };

  const handleSave = async () => {
    let hasError = false;

    if (!oldPassword) {
      setOldPasswordError(intl.formatMessage({ id: 'OldPasswordRequired' }));
      hasError = true;
    } else {
      setOldPasswordError('');
    }

    const newPasswordError = validatePassword(newPassword);
    if (newPasswordError) {
      setNewPasswordError(newPasswordError);
      hasError = true;
    } else {
      setNewPasswordError('');
    }

    if (newPassword !== confirmPassword) {
      setConfirmPasswordError(
        intl.formatMessage({ id: 'PasswordsDoNotMatch' })
      );
      hasError = true;
    } else {
      setConfirmPasswordError('');
    }

    if (!hasError) {
      const result = await UpdatePassword({
        oldPassword,
        newPassword,
      });
      if (result.SetPasswordResult) {
        addToast({
          title: intl.formatMessage({ id: 'passwordUpdateNotification' }),
          type: 'success',
        });
        cancel();
        logout();
      } else {
        addToast({
          title: intl.formatMessage({ id: 'passwordUpdateError' }),
          type: 'error',
        });
      }
    }
  };

  const handleOldPasswordBlur = () => {
    if (!oldPassword) {
      setOldPasswordError(intl.formatMessage({ id: 'OldPasswordRequired' }));
    } else {
      setOldPasswordError('');
    }
  };
  const handleNewPasswordBlur = () => {
    const error = validatePassword(newPassword);
    setNewPasswordError(error);
  };
  const handleConfirmPasswordBlur = () => {
    if (confirmPassword && newPassword !== confirmPassword) {
      setConfirmPasswordError(
        intl.formatMessage({ id: 'PasswordsDoNotMatch' })
      );
    } else {
      setConfirmPasswordError('');
    }
  };

  const cancel = () => {
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setOldPasswordError('');
    setNewPasswordError('');
    setConfirmPasswordError('');
    handleCancel();
  };

  const handleTogglePasswordVisibility = () => {
    //setShowNewPassword(!showNewPassword);
    setShowNewPassword((prevState) => !prevState);
  };

  const handleToggleOldPasswordVisibility = () => {
    setShowOldPassword(!showOldPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Update input types when visibility changes
  useEffect(() => {
    setNewPasswordType(showNewPassword ? 'text' : 'password');
    //setConfirmPasswordType(showConfirmPassword ? 'text' : 'password');
  }, [showNewPassword]);

  return (
    <CustomDialog open={open} maxWidth="sm" fullWidth={true} ref={dialogRef}>
      <StyledDialogTitle>
        <StyledIconButton aria-label="close" onClick={cancel}>
          <CloseIcon />
        </StyledIconButton>
        {intl.formatMessage({ id: 'ChangePassword' })}
      </StyledDialogTitle>
      <StyledDialogContent>
        <DialogContentText
          dangerouslySetInnerHTML={{ __html: passwordRulesString }}
        />
        <CustomeTextField
          id="old-password"
          fullWidth
          label={intl.formatMessage({ id: 'OldPassword' })}
          value={oldPassword}
          color="secondary"
          error={Boolean(oldPasswordError)}
          helperText={oldPasswordError}
          onChange={(e) => setOldPassword(e.target.value)}
          onBlur={handleOldPasswordBlur}
          type={showOldPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <EyeIcon onClick={handleToggleOldPasswordVisibility}>
                  {showOldPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                </EyeIcon>
              </InputAdornment>
            ),
          }}
        />
        <CustomeTextField
          id="new-password"
          fullWidth
          label={intl.formatMessage({ id: 'NewPassword' })}
          value={newPassword}
          color="secondary"
          error={Boolean(newPasswordError)}
          helperText={newPasswordError}
          onChange={(e) => setNewPassword(e.target.value)}
          onBlur={handleNewPasswordBlur}
          //type={showNewPassword ? 'text' : 'password'}
          type={newPasswordType}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <EyeIcon onClick={handleTogglePasswordVisibility}>
                  {showNewPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                </EyeIcon>
              </InputAdornment>
            ),
          }}
        />
        <CustomeTextField
          id="confirm-password"
          fullWidth
          label={intl.formatMessage({ id: 'ConfirmNewPassword' })}
          value={confirmPassword}
          color="secondary"
          error={Boolean(confirmPasswordError)}
          helperText={confirmPasswordError}
          onChange={(e) => setConfirmPassword(e.target.value)}
          onBlur={handleConfirmPasswordBlur}
          type={showConfirmPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <EyeIcon onClick={handleToggleConfirmPasswordVisibility}>
                  {showConfirmPassword ? (
                    <VisibilityIcon />
                  ) : (
                    <VisibilityOffIcon />
                  )}
                </EyeIcon>
              </InputAdornment>
            ),
          }}
        />
      </StyledDialogContent>
      <StyledDialogActions>
        <Button variant="outlined" onClick={cancel}>
          {intl.formatMessage({ id: 'Cancel' })}
        </Button>
        <Button variant="outlined" color="secondary" onClick={handleSave}>
          {intl.formatMessage({ id: 'ChangePassword' })}
        </Button>
      </StyledDialogActions>
    </CustomDialog>
  );
};

export default ChangePasswordDialog;
